<script setup lang="ts">
/**
 * Floating preview for one retrieved chunk.
 *
 * Mounted by `MessageBubble` when the user hovers a `[N]` inline ref, and
 * by `RetrievalBlock` when a source row is clicked. The component itself
 * is layout-agnostic — it just renders the hit's metadata + snippet in a
 * card. Positioning is the parent's job (anchored to the trigger via
 * absolute positioning).
 *
 * Inputs are the same shape the backend's SSE `retrieval` frame sends
 * (see `apps/backend/app/chat.py` + `types/chat.ts::Hit`).
 */
import { computed } from "vue";

import {
  SYNTHESIS_SECTION,
  displaySectionTitle,
  type Hit,
} from "../types/chat";
import GroupPill from "./GroupPill.vue";

const props = defineProps<{ hit: Hit }>();

const isSynthesis = computed(
  () => props.hit.section_title === SYNTHESIS_SECTION,
);
const sectionDisplay = computed(() =>
  displaySectionTitle(props.hit.section_title),
);
</script>

<template>
  <div class="citation-popup" role="dialog" aria-label="citation details">
    <header class="citation-popup__header">
      <span class="citation-popup__n">[{{ hit.n }}]</span>
      <span class="citation-popup__title">{{ hit.title }}</span>
      <span class="citation-popup__score"
        >score {{ hit.score.toFixed(3) }}</span
      >
    </header>

    <p v-if="sectionDisplay" class="citation-popup__section">
      <span class="citation-popup__label">
        {{ isSynthesis ? "type" : "section" }}
      </span>
      <code :class="{ 'citation-popup__synthesis': isSynthesis }">{{
        sectionDisplay
      }}</code>
    </p>

    <p v-if="hit.chunk_summary" class="citation-popup__summary">
      {{ hit.chunk_summary }}
    </p>

    <blockquote v-if="hit.snippet" class="citation-popup__snippet">
      {{ hit.snippet }}<span v-if="hit.snippet.length >= 200">…</span>
    </blockquote>

    <p v-if="hit.cites && hit.cites.length" class="citation-popup__cites">
      <span class="citation-popup__label">related</span>
      <span class="citation-popup__cites-list">
        <code v-for="c in hit.cites" :key="c">{{ c }}</code>
      </span>
    </p>

    <footer class="citation-popup__meta">
      <span
        v-if="hit.topics && hit.topics.length"
        class="citation-popup__topics"
      >
        <GroupPill v-for="t in hit.topics" :key="t" :group="t" />
      </span>
      <span v-if="hit.acl && hit.acl.length" class="citation-popup__acls">
        <GroupPill v-for="a in hit.acl" :key="a" :group="a" />
      </span>
      <!-- The `[N]` reference in the surrounding message already opens
           the source on click; we used to repeat that as an `open source ↗`
           link here, but the link lives at the bottom-right of the popup
           and the mouseleave fired the moment the operator tried to move
           the cursor onto it (the popup is hover-anchored to the `[N]`,
           not the link). Keeping just the source path string for indices
           that don't have a URL. -->
      <span v-if="!hit.url" class="citation-popup__source-path">{{
        hit.source
      }}</span>
    </footer>
  </div>
</template>

<style scoped>
.citation-popup {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  font-size: 0.85rem;
  line-height: 1.5;
  max-width: 28rem;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.citation-popup__header {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  padding-bottom: var(--space-2);
  border-bottom: 1px dashed var(--color-border);
}
.citation-popup__n {
  font-weight: 600;
  color: var(--color-accent);
}
.citation-popup__title {
  flex: 1;
  font-weight: 500;
  color: var(--color-ink);
}
.citation-popup__score {
  font-size: 0.75rem;
  color: var(--color-muted);
  font-variant-numeric: tabular-nums;
}
.citation-popup__section {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
}
.citation-popup__label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-muted);
}
.citation-popup__section code {
  font-size: 0.85em;
  background: var(--color-surface-alt);
  padding: 0.05em 0.4em;
  border-radius: var(--radius-sm);
}
/* Synthesis chunks (ADR 0018 Phase A) — distinct background so the
   operator can tell at a glance that the hit is a per-doc overview
   rather than a regular `##` section. */
.citation-popup__synthesis {
  background: var(--color-accent-dim) !important;
  color: var(--color-accent);
  font-weight: 500;
}
.citation-popup__cites {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  flex-wrap: wrap;
}
.citation-popup__cites-list {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.citation-popup__cites-list code {
  font-size: 0.8em;
  background: var(--color-surface-alt);
  padding: 0.05em 0.4em;
  border-radius: var(--radius-sm);
  color: var(--color-muted);
}
.citation-popup__summary {
  margin: 0;
  color: var(--color-ink);
}
.citation-popup__snippet {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  background: var(--color-surface-alt);
  border-left: 3px solid var(--color-accent);
  border-radius: var(--radius-sm);
  font-size: 0.8rem;
  color: var(--color-muted);
  white-space: pre-wrap;
}
.citation-popup__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  font-size: 0.75rem;
}
.citation-popup__topics,
.citation-popup__acls {
  display: inline-flex;
  gap: 0.25rem;
}
.citation-popup__source-path {
  margin-left: auto;
  color: var(--color-muted);
  font-family: var(--font-mono, monospace);
  font-size: 0.7rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 12rem;
}
</style>
